import { DocumentV2, mediaTypeRegex } from '@eigtech/documents-types'
import { groupBy } from 'lodash-es'
import { useMemo } from 'react'
import { augmentDocument } from '../utils/augmentDocument'
import { sortDocumentsByDate, sortDocumentsByType } from '../utils/sortDocuments'

export type UseOrganizeDocumentsProps = {
  documents: DocumentV2[] | undefined
  filterFn?: (doc: DocumentV2) => boolean
}

const fallbackFilterFn = () => true

export function useBaseOrganizeDocuments({ documents, filterFn }: UseOrganizeDocumentsProps) {
  return useMemo(() => {
    const filteredDocuments = (documents ?? [])
      .filter(filterFn ?? fallbackFilterFn)
      .map(augmentDocument)
    const filenameDocMap = groupBy(filteredDocuments, 'filename')

    const groupedByFilename = Object.values(filenameDocMap).map((group) => {
      const description = group.find((d) => !!d.description)?.description ?? ''
      const items = group.map((doc) => ({ ...doc, description: doc.description || description }))

      return {
        ...items[0],
        subRows: items.slice(1),
      }
    })

    const groupedByNotes = groupBy(groupedByFilename, (group) => group.notes || group.filename)

    const grouped = Object.values(groupedByNotes)

    const rows = grouped.map((group) => {
      const flat = group.map((g) => [{ ...g, subRows: [] }, ...g.subRows]).flat()

      if (flat.length === 1) return flat[0]

      const sorted = [...flat].sort(sortDocumentsByDate)

      return {
        ...sorted[0],
        subRows: sorted.slice(1),
      }
    })

    // sort by date then push dec/loss to top
    rows
      .sort(sortDocumentsByDate)
      .sort(sortDocumentsByType('loss'))
      .sort(sortDocumentsByType('dec'))

    return rows
  }, [documents, filterFn])
}

export function useOrganizeDocuments(documents: UseOrganizeDocumentsProps['documents']) {
  return useBaseOrganizeDocuments({ documents, filterFn: documentFilterFn })
}

export function useOrganizeMedia(documents: UseOrganizeDocumentsProps['documents']) {
  return useBaseOrganizeDocuments({ documents, filterFn: mediaFilterFn })
}

export const mediaFilterFn = (doc: DocumentV2) => !!doc.filename?.match(mediaTypeRegex)
export const documentFilterFn = (doc: DocumentV2) => !mediaFilterFn(doc)
